/**
 * Utility for tracking recently-visited items in the command palette.
 * Items are stored in localStorage and are persisted across sessions.
 *
 * Usage:
 *   - Call addRecentItem(item) when a user navigates to a case/rule/page.
 *   - Call getRecentItems() to retrieve the most-recent items for display.
 */

export interface RecentItem {
  kind: "Action" | "Nav" | "Case" | "Rule" | "Doc";
  label: string;
  to: string;
  visitedAt: number;
}

const STORAGE_KEY = "alpha:recentItems";
const MAX_STORED = 20;
export const RECENT_DISPLAY_LIMIT = 5;

export function getRecentItems(): RecentItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as RecentItem[];
  } catch {
    return [];
  }
}

export function addRecentItem(item: Omit<RecentItem, "visitedAt">): void {
  try {
    const existing = getRecentItems();
    // Remove any previous entry for the same destination to avoid duplicates.
    const deduped = existing.filter((r) => r.to !== item.to);
    const updated: RecentItem[] = [
      { ...item, visitedAt: Date.now() },
      ...deduped,
    ].slice(0, MAX_STORED);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // localStorage unavailable (e.g. private browsing with storage blocked) — silently ignore.
  }
}
